import { type InfiniteData } from '@tanstack/react-query';

import { type PagingParams } from '@app/client';
import { type NFTItem, type NFTManagerFilter } from '@app/store/types/nft-manager';

export const isEnterKey = (key: string) => key === 'Enter';

export function hasImageError(image?: string): Promise<boolean> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(false);
        img.onerror = () => resolve(true);
        if (image) img.src = image;
        else {
            resolve(true);
        }
    });
}

const getVideos = (key: string): string[] => {
    const sessionStr = sessionStorage.getItem(key);
    if (sessionStr) return JSON.parse(sessionStr);
    return [];
};

export async function hasVideoError(key: string, video?: string): Promise<boolean> {
    return new Promise((resolve) => {
        const videos = getVideos(key);
        if (videos.find((vid) => video === vid)) {
            // video has already loaded
            return resolve(false);
        }

        const vid = document.createElement('video');
        vid.onerror = () => {
            vid.remove();
            return resolve(true);
        };
        vid.onloadedmetadata = () => {
            // video has not loaded yet, save to session storage
            const loadedVideos = getVideos(key);
            sessionStorage.setItem(key, JSON.stringify([...loadedVideos, video]));

            vid.remove();
            return resolve(false);
        };
        if (video) vid.src = video;
        else {
            resolve(true);
        }
    });
}

export const getItemById = (
    id: string,
    data: InfiniteData<{
        data: NFTItem[];
        paging: PagingParams;
    }>,
) => {
    let item: NFTItem | undefined;
    if (data?.pages?.length) {
        for (let index = 0; index < data?.pages?.length || 0; index++) {
            const page = data?.pages[index];
            const pageItem = page.data.find((item) => item.id == id);
            if (pageItem) {
                item = pageItem;
                break;
            }
        }
    }
    return item;
};

export const getFiltersCount = (filters: NFTManagerFilter) => {
    return Object.keys(filters).reduce((accumulator, filter) => {
        // Ignore sorting fields
        if (filter !== 'sortingField' && filter !== 'sortingOrder') {
            // filter spam and unsupported are boolean filters
            if ((filter == 'spam' || filter == 'unsupported') && filters[filter]) {
                return accumulator + 1;
            }
            // Checking filter length in case filter is an array
            if (filters[filter]?.length) {
                return accumulator + 1;
            }
        }
        return accumulator;
    }, 0);
};
